import { useState } from "react";
import { motion } from "framer-motion";
import { X, Check } from "lucide-react";
import { DEAL_CHOICES } from "../data/site";

const selectCls =
  "w-full border border-charcoal/20 bg-white/60 px-4 py-3 text-sm text-charcoal focus:border-ember focus:outline-none";

export default function DealModal({ item, onClose, onAdd }) {
  const config = DEAL_CHOICES[item.name] || { selections: [], hint: "Anything else we should know? (optional)" };
  const addons = config.addons || [];
  const [variant, setVariant] = useState(0);
  const [choices, setChoices] = useState(config.selections.map(() => ""));
  const [addonSel, setAddonSel] = useState({});
  const [notes, setNotes] = useState("");
  const ready = choices.every(Boolean);

  const toggleAddon = (key, name, multi) => {
    setAddonSel((prev) => {
      const cur = prev[key] || [];
      if (multi) {
        return { ...prev, [key]: cur.includes(name) ? cur.filter((n) => n !== name) : [...cur, name] };
      }
      return { ...prev, [key]: cur.includes(name) ? [] : [name] };
    });
  };

  const addonsCost = addons.reduce(
    (sum, g) => sum + (addonSel[g.key] || []).reduce((s, name) => s + g.options.find((o) => o.name === name).price, 0),
    0
  );
  const basePrice = item.variants ? item.variants[variant].price : item.price;
  const price = (parseFloat(basePrice) + addonsCost).toFixed(2);

  const confirm = () => {
    const parts = [];
    if (item.variants) parts.push(`Size: ${item.variants[variant].label}`);
    config.selections.forEach((s, i) => parts.push(`${s.label}: ${choices[i]}`));
    addons.forEach((g) => {
      (addonSel[g.key] || []).forEach((name) => parts.push(`${name} +$1`));
    });
    if (notes.trim()) parts.push(`Notes: ${notes.trim()}`);
    onAdd(item, parts.join(" · "), price);
  };

  return (
    <>
      <motion.div
        data-testid="deal-modal-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="fixed inset-0 z-[97] bg-charcoal/50 backdrop-blur-sm"
      />
      <motion.div
        data-testid="deal-modal"
        initial={{ opacity: 0, scale: 0.95, x: "-50%", y: "-46%" }}
        animate={{ opacity: 1, scale: 1, x: "-50%", y: "-50%" }}
        exit={{ opacity: 0, scale: 0.97, x: "-50%", y: "-46%" }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="fixed left-1/2 top-1/2 z-[98] max-h-[85vh] w-[92%] max-w-md overflow-y-auto border border-black/10 bg-cream p-7 shadow-2xl"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-serif text-2xl font-semibold">{item.name}</p>
            {item.desc && <p className="mt-1 text-sm text-charcoal/60">{item.desc}</p>}
          </div>
          <button data-testid="deal-modal-close" onClick={onClose} aria-label="Close" className="shrink-0 text-charcoal/50 transition-colors hover:text-ember">
            <X size={22} />
          </button>
        </div>

        <div className="mt-6 space-y-4">
          {item.variants && (
            <div>
              <p className="mb-1.5 text-xs font-bold uppercase tracking-[0.2em] text-charcoal/50">Size</p>
              <div className="flex flex-wrap gap-2">
                {item.variants.map((v, i) => (
                  <button
                    key={v.label}
                    type="button"
                    data-testid={`variant-${i}`}
                    onClick={() => setVariant(i)}
                    className={`rounded-full border px-4 py-2.5 text-xs font-bold uppercase tracking-widest transition-all duration-300 ${
                      variant === i ? "border-ember bg-ember text-cream" : "border-charcoal/20 text-charcoal/60 hover:border-ember hover:text-ember"
                    }`}
                  >
                    {v.label} · ${v.price}
                  </button>
                ))}
              </div>
            </div>
          )}
          {config.selections.map((s, i) => (
            <div key={s.label}>
              <p className="mb-1.5 text-xs font-bold uppercase tracking-[0.2em] text-charcoal/50">{s.label}</p>
              <select
                data-testid={`deal-choice-${i}`}
                className={selectCls}
                value={choices[i]}
                onChange={(e) => setChoices(choices.map((c, j) => (j === i ? e.target.value : c)))}
              >
                <option value="">Choose...</option>
                {s.options.map((o) => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            </div>
          ))}
          {addons.map((g) => (
            <div key={g.key}>
              <p className="mb-1.5 text-xs font-bold uppercase tracking-[0.2em] text-charcoal/50">{g.label}</p>
              <div className="flex flex-wrap gap-2">
                {g.options.map((o) => {
                  const selected = (addonSel[g.key] || []).includes(o.name);
                  return (
                    <button
                      key={o.name}
                      type="button"
                      data-testid={`addon-${g.key}-${o.name.toLowerCase().replace(/\s/g, "-")}`}
                      onClick={() => toggleAddon(g.key, o.name, g.multi)}
                      className={`rounded-full border px-4 py-2 text-xs font-bold tracking-wide transition-all duration-300 ${
                        selected ? "border-ember bg-ember text-cream" : "border-charcoal/20 text-charcoal/60 hover:border-ember hover:text-ember"
                      }`}
                    >
                      {o.name}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          <div>
            <p className="mb-1.5 text-xs font-bold uppercase tracking-[0.2em] text-charcoal/50">Notes</p>
            <input
              data-testid="deal-notes-input"
              className={selectCls}
              placeholder={config.hint}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>

        <button
          data-testid="deal-add-button"
          onClick={confirm}
          disabled={!ready}
          className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-ember px-8 py-4 text-sm font-bold uppercase tracking-widest text-cream transition-all duration-300 hover:bg-[#991B1B] disabled:opacity-40"
        >
          <Check size={16} /> Add to Order — ${price}
        </button>
      </motion.div>
    </>
  );
}
